import {useCallback, useState, type MouseEvent} from 'react';
import {
  useQuotesFirst,
  useQuotingAvailability,
} from '@/matic/features/quoting/hooks/useQuotingAvailability';
import {createPortal} from 'react-dom';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {
  Home,
  Inbox,
  LibraryBig,
  Loader2,
  MonitorDown,
  MoreHorizontal,
  FileSpreadsheet,
  PackageSearch,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import {useToast} from '@/matic/components/ui/toast';
import {useProcurementAvailability} from '@/matic/features/procurement/hooks/useProcurementAvailability';
import {useTriageCount} from '@/matic/features/relay/hooks/useTriageCount';
import {usePinMutations} from '@/matic/features/relay/hooks/usePinMutations';
import {useLibraryPages} from '@/matic/features/relay/hooks/useLibraryPages';
import {useDismissablePortalMenu} from '@/matic/features/relay/hooks/useDismissablePortalMenu';
import {
  encodeScopeParam,
  scopeKeyOf,
  type ScopeItem,
} from '@/matic/features/relay/lib/scope-utils';
import {ScopePickerDialog} from '@/matic/features/relay/components/library/ScopePickerDialog';
import {DesktopShortcutsDialog} from '@/matic/features/relay/components/library/shortcuts/DesktopShortcutsDialog';
import {useDesktopShortcuts} from '@/matic/features/relay/hooks/useDesktopShortcuts';
import {cn} from '@/matic/lib/utils/cn';

/** Width of the PinRow "…" actions menu — passed to useDismissablePortalMenu
 *  so its right-edge viewport clamp matches the rendered panel. */
const PIN_ROW_MENU_WIDTH = 160;

/**
 * The otto-mode sidebar rail body, hosted inside `SidebarNav` (the `SlideSwap`
 * primary): the two top-level destinations — Home and Triage — followed by the
 * user's saved Pages (named Library scopes created via the Scope Picker) and
 * the "+ Page" entry point. The surrounding chrome (forwarding-address card,
 * Settings, the QMS ↔ Relay toggle) stays in `SidebarNav` below it.
 */
type LibraryRailProps = {
  onEmptySpaceClick?: (event: MouseEvent<HTMLElement>) => void;
};

export function LibraryRail({onEmptySpaceClick}: LibraryRailProps) {
  // The AES shell follows the tenant's engine; its frame only renders once that's known.
  const quotesFirst = useQuotesFirst() === true;
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const onTriage = pathname === '/inbox' || pathname.startsWith('/inbox/');
  const onHome = pathname === '/home';
  const onProcurement = pathname.startsWith('/procurement');
  // On a quotes-first build the Quote Desk home is `/`.
  const onQuotes = pathname === '/' || pathname.startsWith('/quotes');
  const onLibrary = pathname === '/library' || pathname.startsWith('/library/');

  const {pages, activeScopeKey} = useLibraryPages();

  const homeActive = !onTriage && onHome;

  const [homeFlashKey, setHomeFlashKey] = useState(0);
  const [triageFlashKey, setTriageFlashKey] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // Covers the gap between the Scope Picker closing (synchronous, on submit)
  // and the Page actually existing — which can sit behind a real await chain
  // (an OS folder-permission prompt, then the create call) with nothing else
  // in the rail showing that anything is happening. Surfaced on the "+ Page"
  // entry point below.
  const [savingPage, setSavingPage] = useState(false);
  const shortcuts = useDesktopShortcuts();
  const {toast} = useToast();

  const triageCount = useTriageCount();
  const {createPin, updatePin, deletePin} = usePinMutations();

  /**
   * Create a Page, and optionally refresh the desktop folder afterwards.
   *
   * Permission is taken before the mutation, straight off the click, so it's
   * still inside the live gesture — see `useDesktopShortcuts.ensureReady`.
   * `writeAfterPinsSettle` handles the rest: it refetches pins before writing
   * because `createPin` invalidates in `onSettled`, which runs after this
   * `await` already resolves.
   *
   * Invoked as `void handleSavePage(...)` from the picker's `onSave`, so
   * errors don't propagate anywhere on their own — caught here and surfaced
   * as a toast rather than left as an unhandled rejection. `ensureReady` and
   * `writeAfterPinsSettle` already report their own failures (denied
   * permission, missing folder) via `reportOutcome`, so this catch is really
   * only reachable from `createPin` itself.
   */
  const handleSavePage = async (
    name: string,
    items: ScopeItem[],
    alsoUpdateDesktop: boolean
  ) => {
    setSavingPage(true);
    try {
      const readyToWrite = alsoUpdateDesktop && (await shortcuts.ensureReady());

      await createPin.mutateAsync({
        label: name,
        kind: 'records',
        payload: {
          refs: items.map((item) => ({
            objectTypeId: item.typeId,
            objectRecordId: item.recordId,
          })),
        },
      });

      handleOpenPage(items);

      if (readyToWrite) await shortcuts.writeAfterPinsSettle();
    } catch {
      toast({title: "Couldn't save that page", variant: 'error'});
    } finally {
      setSavingPage(false);
    }
  };

  const showProcurement = useProcurementAvailability();
  // Runtime: the tenant's quoting engine, not the build (quotes-first has its own entry).
  const {enabled: showQuotes} = useQuotingAvailability();
  const showHome = !quotesFirst;

  const handleHomeClick = () => {
    if (onHome) setHomeFlashKey((key) => key + 1);
    navigate('/home');
  };

  const handleTriageClick = () => {
    if (onTriage) setTriageFlashKey((key) => key + 1);
  };

  const handleLibraryClick = () => {
    navigate('/library');
  };

  const handleOpenPage = (items: ScopeItem[]) => {
    navigate(`/library?scope=${encodeScopeParam(items)}`);
  };

  return (
    <div
      className="flex flex-1 flex-col overflow-y-auto px-2 py-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sidebar-active/50 hover:[&::-webkit-scrollbar-thumb]:bg-sidebar-active [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
      onClick={onEmptySpaceClick}
    >
      {/* Top-level destinations, each its own row (mirrors the collapsed
          rail's stacked icon layout). Home/Triage flash briefly on arrival,
          then settle back into the continuous rail. */}
      <div className="mb-4 flex flex-col gap-1">
        {quotesFirst && (
          <Link
            to="/"
            className={cn(
              'relative flex h-10 w-full items-center gap-2 overflow-hidden rounded px-2.5 text-sm font-semibold no-underline transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
              onQuotes ? 'text-white' : 'text-sidebar-fg/80'
            )}
          >
            <FileSpreadsheet className="h-5 w-5 shrink-0" />
            <span>Quotes</span>
          </Link>
        )}
        {showHome && (
          <button
            type="button"
            onClick={handleHomeClick}
            className={cn(
              'relative flex h-10 w-full items-center gap-2 overflow-hidden rounded px-2.5 text-left transition-colors',
              homeActive
                ? 'library-nav-flash text-white'
                : 'text-sidebar-fg/80 hover:bg-white/[0.07] hover:text-white'
            )}
          >
            {homeFlashKey > 0 && (
              <span
                key={homeFlashKey}
                className="library-nav-flash pointer-events-none absolute inset-0 rounded"
              />
            )}
            <Home className="h-5 w-5 shrink-0" />
            <span className="truncate text-sm font-semibold">Home</span>
          </button>
        )}
        <Link
          to="/inbox"
          onClick={handleTriageClick}
          className={cn(
            'relative flex h-10 w-full items-center gap-2 overflow-hidden rounded px-2.5 text-sm font-semibold no-underline transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
            onTriage ? 'library-nav-flash text-white' : 'text-sidebar-fg/80'
          )}
        >
          {triageFlashKey > 0 && (
            <span
              key={triageFlashKey}
              className="library-nav-flash pointer-events-none absolute inset-0 rounded"
            />
          )}
          <Inbox className="h-5 w-5 shrink-0" />
          <span>Inbox</span>
          {triageCount > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-white/15 px-1 text-[10px] font-semibold text-sidebar-fg">
              {triageCount > 99 ? '99+' : triageCount}
            </span>
          )}
        </Link>
        {showProcurement && !quotesFirst && (
          <Link
            to="/procurement"
            className={cn(
              'relative flex h-10 w-full items-center gap-2 overflow-hidden rounded px-2.5 text-sm font-semibold no-underline transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
              onProcurement ? 'text-white' : 'text-sidebar-fg/80'
            )}
          >
            <PackageSearch className="h-5 w-5 shrink-0" />
            <span>Procurement</span>
          </Link>
        )}
        {showQuotes && !quotesFirst && (
          <Link
            to="/quotes"
            className={cn(
              'relative flex h-10 w-full items-center gap-2 overflow-hidden rounded px-2.5 text-sm font-semibold no-underline transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
              onQuotes ? 'text-white' : 'text-sidebar-fg/80'
            )}
          >
            <FileSpreadsheet className="h-5 w-5 shrink-0" />
            <span>Quotes</span>
          </Link>
        )}
        <button
          type="button"
          onClick={handleLibraryClick}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded px-2.5 text-left text-sm font-semibold transition-colors',
            onLibrary && activeScopeKey === ''
              ? 'bg-white/[0.16] text-white'
              : 'text-sidebar-fg/80 hover:bg-white/[0.07] hover:text-white'
          )}
        >
          <LibraryBig className="h-5 w-5 shrink-0" />
          <span>Library</span>
        </button>
      </div>

      {/* Saved Pages — named Library scopes. */}
      <div className="flex flex-col gap-0.5">
        {pages.length > 0 &&
          pages.map((page) => (
            <PinRow
              key={page.id}
              name={page.name}
              isActive={
                activeScopeKey !== '' &&
                activeScopeKey === scopeKeyOf(page.items)
              }
              onOpen={() => {
                handleOpenPage(page.items);
              }}
              onRename={(name) => {
                updatePin.mutate({pinId: page.id, label: name});
              }}
              onRemove={() => {
                deletePin.mutate(page.id);
              }}
            />
          ))}
      </div>

      <div className="group mt-0.5 flex items-center gap-0.5">
        <button
          type="button"
          disabled={savingPage}
          onClick={() => {
            setPickerOpen(true);
          }}
          className="flex min-w-0 flex-1 items-center gap-2 rounded px-2.5 py-1.5 text-left text-sm font-medium text-sidebar-fg/55 transition-colors hover:bg-white/[0.07] hover:text-white disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:text-sidebar-fg/55"
        >
          {savingPage ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 shrink-0" />
          )}
          <span>{savingPage ? 'Saving…' : 'Page'}</span>
        </button>
        {/* The folder on disk mirrors the Pages above it, so its entry point
            belongs here rather than in the Library header. */}
        <button
          type="button"
          aria-label="Desktop shortcuts"
          onClick={() => {
            setShortcutsOpen(true);
          }}
          className="flex h-7 w-7 items-center justify-center rounded text-sidebar-fg/45 opacity-0 transition-opacity hover:bg-white/[0.07] hover:text-white focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 group-hover:opacity-100"
        >
          <MonitorDown className="h-4 w-4" />
        </button>
      </div>

      <DesktopShortcutsDialog
        open={shortcutsOpen}
        isSupported={shortcuts.isSupported}
        hasFolder={shortcuts.hasFolder}
        folderName={shortcuts.folderName}
        pages={shortcuts.pages}
        views={shortcuts.availableViews}
        selectedViewKeys={shortcuts.selectedViewKeys}
        selectedViewCount={shortcuts.selectedViewCount}
        onViewSelectedChange={shortcuts.setViewSelected}
        format={shortcuts.format}
        onFormatChange={shortcuts.setFormat}
        isWorking={shortcuts.isWorking}
        progress={shortcuts.progress}
        onClose={() => {
          setShortcutsOpen(false);
        }}
        onChooseFolder={() => {
          void shortcuts.chooseFolderAndWrite();
        }}
        onUpdate={() => {
          void shortcuts.updateInPlace();
        }}
        onDownloadZip={shortcuts.downloadZip}
      />

      {pickerOpen && (
        <ScopePickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          canUpdateDesktop={shortcuts.hasFolder}
          onSave={(name, items, alsoUpdateDesktop) => {
            void handleSavePage(name, items, alsoUpdateDesktop);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PinRow — one saved Page: name (inline-renameable) + a single hover-revealed
// "…" menu (Rename / Remove). The menu is absolutely positioned so it never
// reserves layout space — the name only yields room for it while the row is
// actually hovered/focused/open, instead of permanently truncating early.
// ---------------------------------------------------------------------------

type PinRowProps = {
  name: string;
  isActive: boolean;
  onOpen: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
};

function PinRow({name, isActive, onOpen, onRename, onRemove}: PinRowProps) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);
  const {position, triggerRef, panelRef} = useDismissablePortalMenu({
    isOpen: menuOpen,
    menuWidth: PIN_ROW_MENU_WIDTH,
    onDismiss: closeMenu,
  });

  const commitRename = () => {
    const next = draftName.trim();
    if (next && next !== name) onRename(next);
    setEditing(false);
  };

  return (
    <div
      className={cn(
        'group relative flex items-center rounded pl-2.5 transition-colors',
        isActive
          ? 'bg-white/[0.12] text-white shadow-[inset_2px_0_0_#FF6B35]'
          : 'text-sidebar-fg/80 hover:bg-white/[0.07] hover:text-white'
      )}
    >
      {editing ? (
        <input
          autoFocus
          aria-label={`Rename ${name}`}
          value={draftName}
          onChange={(event) => {
            setDraftName(event.target.value);
          }}
          onBlur={commitRename}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commitRename();
            if (event.key === 'Escape') {
              setDraftName(name);
              setEditing(false);
            }
          }}
          className="min-w-0 flex-1 rounded bg-white/10 px-1.5 py-1 text-sm text-white outline-none ring-1 ring-primary-500"
        />
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'min-w-0 flex-1 truncate py-1.5 pr-1 text-left text-sm transition-[padding-right] duration-150 group-hover:pr-7',
            menuOpen && 'pr-7',
            isActive && 'font-semibold'
          )}
        >
          {name}
        </button>
      )}

      {!editing && (
        // Wrapped in its own div so the flyout anchors to the "…" button
        // itself (matching CollapsedSidebar's flyout positioning), not the
        // full-width row.
        <div
          ref={triggerRef}
          className="absolute right-0.5 top-1/2 -translate-y-1/2"
        >
          <button
            type="button"
            title="Page actions"
            aria-label={`Actions for ${name}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded text-sidebar-fg/45 opacity-0 transition-opacity hover:text-white focus:opacity-100 group-hover:opacity-100',
              menuOpen && 'text-white opacity-100'
            )}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {menuOpen &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            aria-label={`Actions for ${name}`}
            style={{left: position.left, top: position.top}}
            className="fixed z-50 w-40 overflow-hidden rounded-lg border border-primary-500/30 bg-sidebar-bg py-1 shadow-[0_8px_32px_rgba(0,0,0,0.6)]"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setDraftName(name);
                setEditing(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white transition-colors hover:bg-white/10"
            >
              <Pencil className="h-3.5 w-3.5 text-sidebar-fg/50" />
              Rename
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                onRemove();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-400 transition-colors hover:bg-red-500/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}
